import { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FaArrowRight,
  FaBookOpen,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaList,
  FaSearch,
  FaThLarge,
  FaTimes,
} from "react-icons/fa";
import CryptoJS from "crypto-js";
import { motion } from "framer-motion";

import CourseCard from "../../Components/CourseCard";
import ScrollingCourseTitle from "../../Components/ScrollingCourseTitle";
import axiosInstance from "../../Context/axiosInstance.js";
import { visibleItems } from "../../utils/visibility";
import { DarkModeContext } from "../../Context/DarkModeContext.jsx";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { getSearchProbe, matchesSearch } from "../../utils/search";

const pageSizes = [10, 20, 30, 50];
const grid =
  "mx-auto grid w-full max-w-[230px] grid-cols-1 items-start gap-5 md:max-w-[730px] md:grid-cols-3 lg:max-w-[1480px] lg:grid-cols-5";
const focus =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[var(--study-canvas)]";

export default function UserCoursesPage() {
  const { isDarkMode } = useContext(DarkModeContext);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number.parseInt(params.get("page"), 10) || 1);
  const requestedSize = Number(params.get("pageSize"));
  const pageSize = pageSizes.includes(requestedSize) ? requestedSize : 20;
  const searchQuery = params.get("search") || "";
  const normalizedSearch = searchQuery.trim().replace(/\s+/g, " ");
  const debouncedSearch = useDebouncedValue(normalizedSearch);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [retry, setRetry] = useState(0);
  const [view, setView] = useState("list");
  const userId = localStorage.getItem("userId");
  const pending = loading || normalizedSearch !== debouncedSearch;
  const surface = isDarkMode
    ? "border-[var(--study-border)] bg-[var(--study-surface)]"
    : "border-slate-200 bg-white";
  const muted = isDarkMode ? "text-[var(--study-muted)]" : "text-slate-500";
  const primary = isDarkMode
    ? "bg-red-700 hover:bg-red-800 text-white"
    : "bg-[#06315B] hover:bg-[#0b477f] text-white";

  useEffect(() => {
    if (normalizedSearch !== debouncedSearch) return;
    const controller = new AbortController();
    let active = true;
    const fetchCourses = async () => {
      setLoading(true);
      setError("");
      try {
        const decryptedId = CryptoJS.AES.decrypt(
          userId || "",
          process.env.REACT_APP_SECRET_KEY,
        ).toString(CryptoJS.enc.Utf8);
        if (!decryptedId) throw new Error("Missing user");
        const { data } = await axiosInstance.get(
          `/users/${decryptedId}/courses`,
          {
            params: {
              page,
              pageSize,
              status: "purchased",
              search: getSearchProbe(debouncedSearch),
            },
            signal: controller.signal,
          },
        );
        if (!active) return;
        const returnedCourses = visibleItems(data.response || []);
        setCourses(
          debouncedSearch
            ? returnedCourses.filter((course) =>
                matchesSearch(course, debouncedSearch, ["name", "gradeLevel"]),
              )
            : returnedCourses,
        );
        setTotalPages(
          Math.max(1, Math.ceil((Number(data.totalCount) || 0) / pageSize)),
        );
      } catch {
        if (active)
          setError("We couldn't load your courses. Please try again.");
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchCourses();
    return () => {
      active = false;
      controller.abort();
    };
  }, [userId, page, pageSize, debouncedSearch, normalizedSearch, retry]);

  const updateParams = (values, replace = false) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([key, value]) =>
      value === "" ? next.delete(key) : next.set(key, String(value)),
    );
    setParams(next, { replace });
  };
  const openCourse = (course) => {
    const slug = encodeURIComponent(
      course.name.replace(/\s+/g, "_").replace(/\//g, "-").toLowerCase(),
    );
    navigate(`/my-courses/${slug}`, { state: { courseId: course.id, course } });
  };
  const browse = () => navigate("/student-folders");
  const pageNumbers = [
    ...new Set([
      1,
      ...Array.from({ length: 3 }, (_, index) => page - 1 + index).filter(
        (number) => number > 1 && number < totalPages,
      ),
      totalPages,
    ]),
  ].sort((a, b) => a - b);

  return (
    <section
      className={`min-h-screen px-4 pb-10 pt-24 sm:px-6 lg:px-8 ${isDarkMode ? "bg-[var(--study-canvas)] text-[var(--study-text)]" : "bg-[#f6f7f9] text-slate-900"}`}
    >
      <header className="mb-8 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em]">
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-lg ${isDarkMode ? "bg-white/5 text-red-300" : "bg-red-50 text-red-600"}`}
            >
              <FaBookOpen aria-hidden="true" />
            </span>
            <span className={muted}>Your learning library</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            My Courses
          </h1>
          <p className={`mt-3 max-w-lg text-sm leading-relaxed ${muted}`}>
            A little progress, every day. Choose a course and make time to
            learn.
          </p>
        </div>
        <button
          type="button"
          onClick={browse}
          className={`inline-flex min-h-[46px] items-center justify-center gap-3 self-start rounded-xl border px-5 text-sm font-semibold transition-colors sm:self-auto ${surface} ${focus} hover:border-slate-400`}
        >
          Explore courses <FaArrowRight aria-hidden="true" />
        </button>
      </header>

      <div
        className={`mb-6 flex flex-col gap-4 rounded-2xl border p-4 sm:flex-row sm:items-center ${surface}`}
      >
        <div className="relative min-w-0 flex-1">
          <FaSearch
            aria-hidden="true"
            className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${muted}`}
          />
          <input
            type="search"
            aria-label="Search purchased courses"
            placeholder="Find a course in your library..."
            value={searchQuery}
            onChange={(event) =>
              updateParams({ search: event.target.value, page: 1 }, true)
            }
            className={`min-h-[48px] w-full rounded-xl border py-3 pl-11 pr-12 text-sm [&::-webkit-search-cancel-button]:appearance-none ${isDarkMode ? "border-[var(--study-input-border)] bg-[var(--study-canvas)]" : "border-slate-200 bg-slate-50"} ${focus}`}
          />
          {searchQuery && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => updateParams({ search: "", page: 1 }, true)}
              className={`absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-lg ${muted} ${focus}`}
            >
              <FaTimes aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between gap-4">
          <span aria-live="polite" className={`text-xs ${muted}`}>
            {pending
              ? "Updating library..."
              : error
                ? "Library unavailable"
                : `${courses.length} ${courses.length === 1 ? "course" : "courses"} on this page`}
          </span>
          <div
            role="group"
            aria-label="Course layout"
            className={`flex shrink-0 gap-1 rounded-xl border p-1 ${isDarkMode ? "border-[var(--study-border)]" : "border-slate-200"}`}
          >
            {[
              { value: "list", Icon: FaList, label: "List view" },
              { value: "grid", Icon: FaThLarge, label: "Grid view" },
            ].map(({ value, Icon, label }) => (
              <button
                key={value}
                type="button"
                aria-label={label}
                title={label}
                aria-pressed={view === value}
                onClick={() => setView(value)}
                className={`flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${focus} ${view === value ? (isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-900") : muted}`}
              >
                <Icon aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div aria-busy={pending} className="min-h-[360px]">
        {pending ? (
          <>
            <span role="status" className="sr-only">
              Loading your courses
            </span>
            <div
              aria-hidden="true"
              className={view === "grid" ? grid : "space-y-3"}
            >
              {Array.from({ length: Math.min(pageSize, 10) }, (_, index) => (
                <div
                  key={index}
                  className={`overflow-hidden rounded-2xl border p-4 motion-safe:animate-pulse ${surface} ${view === "list" ? "flex items-center gap-4" : ""}`}
                >
                  <div
                    className={`rounded-xl ${isDarkMode ? "bg-white/5" : "bg-slate-100"} ${view === "grid" ? "mb-5 aspect-[1.58]" : "h-20 w-28 shrink-0"}`}
                  />
                  <div className="flex-1 space-y-3">
                    <div
                      className={`h-4 w-3/4 rounded ${isDarkMode ? "bg-white/10" : "bg-slate-200"}`}
                    />
                    <div
                      className={`h-3 w-1/2 rounded ${isDarkMode ? "bg-white/5" : "bg-slate-100"}`}
                    />
                    <div
                      className={`h-10 rounded-lg ${isDarkMode ? "bg-white/5" : "bg-slate-100"}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : error ? (
          <div
            role="alert"
            className={`flex min-h-[360px] flex-col items-center justify-center rounded-2xl border px-6 py-12 text-center ${surface}`}
          >
            <FaBookOpen
              aria-hidden="true"
              className={`mb-5 text-3xl ${muted}`}
            />
            <h2 className="text-xl font-bold">Your library couldn't load</h2>
            <p className={`mt-2 text-sm ${muted}`}>{error}</p>
            <button
              type="button"
              onClick={() => setRetry((value) => value + 1)}
              className={`mt-6 rounded-xl px-6 py-3 text-sm font-semibold ${primary} ${focus}`}
            >
              Try again
            </button>
          </div>
        ) : courses.length === 0 ? (
          <div
            className={`flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-12 text-center ${surface}`}
          >
            <span
              className={`mb-5 flex h-16 w-16 items-center justify-center rounded-2xl ${isDarkMode ? "bg-white/5" : "bg-slate-100"}`}
            >
              {debouncedSearch ? (
                <FaSearch aria-hidden="true" className="text-2xl" />
              ) : (
                <FaBookOpen aria-hidden="true" className="text-2xl" />
              )}
            </span>
            <h2 className="text-xl font-bold">
              {debouncedSearch
                ? "No matching courses"
                : page > 1
                  ? "No courses on this page"
                  : "Your next chapter starts here"}
            </h2>
            <p
              className={`mt-2 max-w-md break-words text-sm leading-relaxed ${muted}`}
            >
              {debouncedSearch
                ? `No courses found for "${debouncedSearch}". Try a different title.`
                : page > 1
                  ? "Return to the first page to see your library."
                  : "Courses you purchase or claim will appear here, ready whenever you are."}
            </p>
            <button
              type="button"
              onClick={
                debouncedSearch
                  ? () => updateParams({ search: "", page: 1 }, true)
                  : page > 1
                    ? () => updateParams({ page: 1 })
                    : browse
              }
              className={`mt-6 inline-flex items-center gap-3 rounded-xl px-6 py-3 text-sm font-semibold ${primary} ${focus}`}
            >
              {debouncedSearch
                ? "Clear search"
                : page > 1
                  ? "Go to first page"
                  : "Explore courses"}
              <FaArrowRight aria-hidden="true" />
            </button>
          </div>
        ) : view === "grid" ? (
          <motion.div
            className={grid}
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: {
                transition: {
                  staggerChildren: 0.06,
                },
              },
            }}
          >
            {courses.map((course) => (
              <motion.div
                key={course.id}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                transition={{
                  duration: 0.35,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <CourseCard
                  course={course}
                  owned
                  isDarkMode={isDarkMode}
                  onOpen={() => openCourse(course)}
                />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            className="space-y-3"
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: {
                transition: {
                  staggerChildren: 0.06,
                },
              },
            }}
          >
            {courses.map((course) => (
              <motion.article
                key={course.id}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0 },
                }}
                transition={{
                  duration: 0.35,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className={`flex flex-wrap items-center gap-4 rounded-2xl border p-4 transition-shadow hover:shadow-md sm:flex-nowrap ${surface}`}
              >
                <img
                  src={
                    course.courseImageUrl ||
                    course.imageUrl ||
                    "/default-course.jpg"
                  }
                  alt=""
                  className="h-20 w-24 shrink-0 rounded-xl object-cover sm:w-32"
                  onError={(event) => {
                    if (
                      !event.currentTarget.src.endsWith("/default-course.jpg")
                    )
                      event.currentTarget.src = "/default-course.jpg";
                  }}
                />
                <div className="min-w-0 flex-1">
                  <ScrollingCourseTitle
                    as="h2"
                    title={course.name}
                    className="text-base font-bold"
                  />
                  <span
                    className={`mt-2 inline-flex items-center gap-1.5 text-xs ${isDarkMode ? "text-emerald-300" : "text-emerald-700"}`}
                  >
                    <FaCheckCircle aria-hidden="true" />
                    Unlocked
                  </span>
                </div>
                <button
                  type="button"
                  aria-label={`Open ${course.name}`}
                  onClick={() => openCourse(course)}
                  className={`inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold sm:w-auto ${primary} ${focus}`}
                >
                  Open Course
                  <FaArrowRight aria-hidden="true" />
                </button>
              </motion.article>
            ))}
          </motion.div>
        )}
      </div>

      <footer
        className={`mt-8 flex flex-col gap-4 border-t pt-5 sm:flex-row sm:items-center sm:justify-between ${isDarkMode ? "border-[var(--study-border)]" : "border-slate-200"}`}
      >
        <label className={`flex items-center gap-3 text-sm ${muted}`}>
          Courses per page
          <select
            aria-label="Courses per page"
            value={pageSize}
            disabled={pending}
            onChange={(event) =>
              updateParams({ pageSize: event.target.value, page: 1 })
            }
            className={`min-h-[42px] rounded-lg border px-3 ${surface} ${focus}`}
          >
            {pageSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        {!error && (
          <nav
            aria-label="Course pages"
            className="flex flex-wrap items-center gap-1.5"
          >
            <button
              type="button"
              aria-label="Previous page"
              disabled={pending || page <= 1}
              onClick={() => updateParams({ page: page - 1 })}
              className={`flex h-10 w-10 items-center justify-center rounded-lg border disabled:opacity-40 ${surface} ${focus}`}
            >
              <FaChevronLeft aria-hidden="true" />
            </button>
            {pageNumbers.map((number, index) => (
              <span key={number} className="inline-flex items-center gap-1.5">
                {index > 0 && number - pageNumbers[index - 1] > 1 && (
                  <span aria-hidden="true" className={muted}>
                    ...
                  </span>
                )}
                <button
                  type="button"
                  aria-label={`Page ${number}`}
                  aria-current={page === number ? "page" : undefined}
                  disabled={pending}
                  onClick={() => updateParams({ page: number })}
                  className={`h-10 min-w-[40px] rounded-lg px-3 text-sm font-semibold disabled:opacity-50 ${page === number ? primary : surface} ${focus}`}
                >
                  {number}
                </button>
              </span>
            ))}
            <button
              type="button"
              aria-label="Next page"
              disabled={pending || page >= totalPages}
              onClick={() => updateParams({ page: page + 1 })}
              className={`flex h-10 w-10 items-center justify-center rounded-lg border disabled:opacity-40 ${surface} ${focus}`}
            >
              <FaChevronRight aria-hidden="true" />
            </button>
          </nav>
        )}
      </footer>
    </section>
  );
}
